import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import type { User } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { useApiRoot } from '../contexts/ApiRootContext';
import PrimaryNavLinks from './PrimaryNavLinks';
import UserAccountMenu from './UserAccountMenu';

function accountDisplayName(user: User): string {
  const name = user.displayName?.trim();
  if (name) return name;
  if (user.email) return user.email.split('@')[0] ?? user.email;
  return 'User';
}

function CompactNavMenu() {
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { hasLink } = useApiRoot();
  const { pathname } = useLocation();
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const showMyAra = hasLink('my');
  const fullName = user ? accountDisplayName(user) : null;
  const email = user?.email ?? null;

  return (
    <div className="relative shrink-0 md:hidden" ref={rootRef}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="true"
        aria-label={open ? 'Close menu' : 'Open menu'}
        onClick={() => setOpen((value) => !value)}
        className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
      >
        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 w-64 pt-1">
          <div
            id={menuId}
            role="menu"
            className="rounded-xl border border-gray-200 bg-white py-2 shadow-lg ring-1 ring-black/5"
          >
            {user && (
              <div className="px-4 py-3">
                <p className="text-sm font-semibold text-gray-900 break-words">{fullName}</p>
                {email && <p className="mt-1 text-xs text-gray-500 break-all">{email}</p>}
                {!email && <p className="mt-1 text-xs text-gray-400">No email on file</p>}
              </div>
            )}
            <div className={`flex flex-col px-2 py-2${user ? ' border-t border-gray-100' : ''}`}>
              <PrimaryNavLinks orientation="vertical" onNavigate={() => setOpen(false)} />
            </div>
            {user && (
              <div className="border-t border-gray-100 px-2 pt-1">
                {showMyAra && (
                  <Link
                    to="/my-ara"
                    role="menuitem"
                    className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                    onClick={() => setOpen(false)}
                  >
                    My ARA
                  </Link>
                )}
                <button
                  type="button"
                  role="menuitem"
                  className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-red-600 transition hover:bg-red-50"
                  onClick={() => {
                    setOpen(false);
                    void signOut();
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppNavActions() {
  return (
    <>
      <div className="hidden shrink-0 space-x-1 md:flex">
        <PrimaryNavLinks />
        <UserAccountMenu />
      </div>
      <CompactNavMenu />
    </>
  );
}
